import api from "../config/api";
import {
  AcceptFeesPayload,
  AcceptTermsPayload,
  ApplicationInfoResponse,
  ApplicationStatusResponse,
  BeginIdvPayload,
  BeginIdvResponse,
  BeneficialOwner,
  CompleteIdvPayload,
  CompleteIdvResponse,
  CreateApplicationPayload,
  CreateApplicationResponse,
  DocumentsResponse,
  FeesResponse,
  SubmitApplicationPayload,
  SubmitApplicationResponse,
  TermsAndConditionsResponse,
} from "@/queries/provider-onboarding/types";

const base = (userId: string, applicationId: string, providerType: string) =>
  `/platform/provider-onboarding/${userId}/${applicationId}/${providerType}`;

export const getApplicationInfo = async (
  userId: string,
  providerType: string,
): Promise<ApplicationInfoResponse> => {
  const { data } = await api.get(
    `/platform/provider-onboarding/${userId}/${providerType}/`,
  );
  return data?.data ?? data;
};

export const createApplication = async (
  userId: string,
  providerType: string,
  payload: CreateApplicationPayload,
): Promise<CreateApplicationResponse> => {
  const { data } = await api.post(
    `/platform/provider-onboarding/create_application/${userId}/${providerType}`,
    payload,
    { timeout: 120_000 },
  );
  return data?.data ?? data;
};

export const getFees = async (
  userId: string,
  applicationId: string,
  providerType: string,
): Promise<FeesResponse> => {
  const { data } = await api.get(
    `${base(userId, applicationId, providerType)}/fees`,
  );
  return data?.data ?? data;
};

export const acceptFees = async (
  userId: string,
  applicationId: string,
  providerType: string,
  payload: AcceptFeesPayload,
): Promise<unknown> => {
  const { data } = await api.post(
    `${base(userId, applicationId, providerType)}/fees`,
    payload,
  );
  return data?.data ?? data;
};

export const getTermsAndConditions = async (
  userId: string,
  applicationId: string,
  providerType: string,
): Promise<TermsAndConditionsResponse> => {
  const { data } = await api.get(
    `${base(userId, applicationId, providerType)}/terms_and_conditions`,
  );
  return data?.data ?? data;
};

export const acceptTermsAndConditions = async (
  userId: string,
  applicationId: string,
  providerType: string,
  payload: AcceptTermsPayload,
): Promise<unknown> => {
  const { data } = await api.post(
    `${base(userId, applicationId, providerType)}/terms_and_conditions`,
    payload,
  );
  return data?.data ?? data;
};

export const getBeneficialOwners = async (
  userId: string,
  applicationId: string,
  providerType: string,
): Promise<BeneficialOwner[]> => {
  const { data } = await api.get(
    `${base(userId, applicationId, providerType)}/beneficial_owners`,
  );
  return data?.data ?? data;
};

export const beginIdv = async (
  userId: string,
  applicationId: string,
  providerType: string,
  payload: BeginIdvPayload,
): Promise<BeginIdvResponse> => {
  const { data } = await api.post(
    `${base(userId, applicationId, providerType)}/begin_idv`,
    payload,
  );
  return data?.data ?? data;
};

export const completeIdv = async (
  userId: string,
  applicationId: string,
  providerType: string,
  payload: CompleteIdvPayload,
): Promise<CompleteIdvResponse> => {
  const { data } = await api.post(
    `${base(userId, applicationId, providerType)}/complete_idv`,
    payload,
  );
  return data?.data ?? data;
};

export const getApplicationStatus = async (
  userId: string,
  applicationId: string,
  providerType: string,
): Promise<ApplicationStatusResponse> => {
  const { data } = await api.get(
    `${base(userId, applicationId, providerType)}/status`,
  );
  return data?.data ?? data;
};

export const submitApplication = async (
  userId: string,
  applicationId: string,
  providerType: string,
  payload: SubmitApplicationPayload,
): Promise<SubmitApplicationResponse> => {
  const { data } = await api.post(
    `/platform/provider-onboarding/submit_application/${userId}/${applicationId}/${providerType}`,
    payload,
  );
  return data?.data ?? data;
};

export const getDocuments = async (
  userId: string,
  applicationId: string,
  providerType: string,
): Promise<DocumentsResponse> => {
  const { data } = await api.get(
    `${base(userId, applicationId, providerType)}/documents`,
  );
  return { url: data?.data ?? data };
};
